import { useEffect, useState } from "react";
import { Alert, App, Button, Card, DatePicker, Form, Input, Space, Spin, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { type UpdateProfileResponse, type User } from "@gull/shared";
import { enduserApi, ApiError } from "../../lib/api";

const { Paragraph } = Typography;

interface ProfileFormValues {
  name: string;
  phone: string;
  email: string;
  address: string;
  postalCode: string;
  city: string;
  bankAccountNumber: string;
  kontoEier: string;
  dateOfBirth: Dayjs | null;
}

export function ProfileTab({ profile }: { profile: User | null | undefined }) {
  const { message } = App.useApp();
  const [form] = Form.useForm<ProfileFormValues>();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!profile) return;
    form.setFieldsValue({
      name: profile.name ?? "",
      phone: profile.phone ?? "",
      email: profile.email ?? "",
      address: profile.address ?? "",
      postalCode: profile.postalCode ?? "",
      city: profile.city ?? "",
      bankAccountNumber: profile.bankAccountNumber ?? "",
      kontoEier: profile.kontoEier ?? profile.name ?? "",
      dateOfBirth: profile.dateOfBirth ? dayjs(profile.dateOfBirth) : null,
    });
  }, [profile, form]);

  if (profile === undefined) return <Spin />;
  if (profile === null) {
    return <Alert type="error" showIcon message="Fant ikke brukerprofil" />;
  }

  const submit = async (values: ProfileFormValues) => {
    const patch: Record<string, string | number | boolean> = {};
    if (values.name && values.name !== (profile.name ?? "")) patch.name = values.name;
    if (values.phone && values.phone !== (profile.phone ?? "")) patch.phone = values.phone;
    if (values.email && values.email !== (profile.email ?? "")) patch.email = values.email;
    if (values.address && values.address !== (profile.address ?? ""))
      patch.address = values.address;
    if (values.postalCode && values.postalCode !== (profile.postalCode ?? "")) {
      patch.postalCode = values.postalCode;
    }
    if (values.city && values.city !== (profile.city ?? "")) patch.city = values.city;
    if (
      values.bankAccountNumber &&
      values.bankAccountNumber !== (profile.bankAccountNumber ?? "")
    ) {
      patch.bankAccountNumber = values.bankAccountNumber;
    }
    if (values.kontoEier && values.kontoEier !== (profile.kontoEier ?? "")) {
      patch.kontoEier = values.kontoEier;
    }
    const dob = values.dateOfBirth ? values.dateOfBirth.valueOf() : null;
    if (dob !== profile.dateOfBirth && dob !== null) {
      patch.dateOfBirth = dob;
    }
    if (Object.keys(patch).length === 0) {
      message.info("Ingen endringer");
      return;
    }
    setSaving(true);
    try {
      await enduserApi.authedPatch<UpdateProfileResponse>("/me", patch);
      message.success("Profil oppdatert");
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Kunne ikke lagre");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Space direction="vertical" size="middle" style={{ display: "flex" }}>
      <Card>
        <Paragraph type="secondary" style={{ marginTop: 0 }}>
          Standard utbetalingskonto brukes som forslag når du starter en ny ordre. Du kan endre
          konto på hver enkelt ordre før tilbudet aksepteres.
        </Paragraph>
        <Form form={form} layout="vertical" onFinish={(v) => void submit(v)} autoComplete="off">
          <Form.Item
            label="Navn"
            name="name"
            rules={[{ required: true, message: "Navn er påkrevd" }, { max: 120 }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Telefon"
            name="phone"
            rules={[
              { required: true, message: "Telefon er påkrevd" },
              { pattern: /^\+?\d{8,15}$/, message: "Ugyldig telefonnummer" },
            ]}
          >
            <Input type="tel" placeholder="+47..." />
          </Form.Item>
          <Form.Item
            label="E-post"
            name="email"
            rules={[
              { required: true, message: "E-post er påkrevd" },
              { type: "email", message: "Ugyldig e-postadresse" },
            ]}
            extra={
              profile.email && !profile.emailVerified
                ? "Ikke bekreftet ennå."
                : profile.emailVerified
                  ? "Bekreftet."
                  : null
            }
          >
            <Input type="email" />
          </Form.Item>
          <Form.Item label="Adresse" name="address" rules={[{ max: 500 }]}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Space size="middle" wrap style={{ display: "flex" }}>
            <Form.Item
              label="Postnummer"
              name="postalCode"
              rules={[{ pattern: /^\d{4}$/, message: "Fire siffer" }]}
              style={{ flex: "0 0 140px" }}
            >
              <Input placeholder="0150" />
            </Form.Item>
            <Form.Item label="Poststed" name="city" style={{ flex: 1, minWidth: 180 }}>
              <Input placeholder="Oslo" />
            </Form.Item>
          </Space>
          <Form.Item
            label="Fødselsdato"
            name="dateOfBirth"
            help="Brukes for ID-kontroll og betaling."
          >
            <DatePicker
              style={{ width: 220 }}
              format="DD. MMMM YYYY"
              disabledDate={(d) => d && d.valueOf() > Date.now()}
            />
          </Form.Item>
          <Form.Item
            label="Standard utbetalingskonto"
            name="bankAccountNumber"
            validateTrigger="onBlur"
            rules={[
              {
                pattern: /^\d{4}\.\d{2}\.\d{5}$|^\d{11}$/,
                message: "Ugyldig kontonummer (11 siffer eller XXXX.XX.XXXXX)",
              },
            ]}
          >
            <Input placeholder="1234.56.78901" />
          </Form.Item>
          <Form.Item
            label="Eier av konto"
            name="kontoEier"
            rules={[{ max: 120, message: "Maks 120 tegn" }]}
          >
            <Input placeholder="Navn på eier av konto" />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={saving}>
            Lagre endringer
          </Button>
        </Form>
      </Card>
    </Space>
  );
}
